import React, { useState, useEffect } from 'react';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import FavoritesPage from './pages/FavoritesPage';
import AllRecipesPage from './pages/AllRecipesPage';
import RecipeDetail from './components/recipe/RecipeDetail';
import RandomRecipeSwiper from './components/recipe/RandomRecipeSwiper';
import CategorySelection from './components/recipe/CategorySelection';
import FridgeRecipeFinder from './components/fridge/FridgeRecipeFinder';
import ThemeRecipesPage from './pages/ThemeRecipesPage';
import AdminDietRecipesPage from './pages/AdminDietRecipesPage';
import AdminPanel from './pages/AdminPanel';
import AdminCategoryRecipesPage from './pages/AdminCategoryRecipesPage';
import BottomNav from './components/layout/BottomNav/BottomNav';
import SplashScreen from './components/SplashScreen';
import { recipes } from './data/recipes';
import { Recipe } from './types/recipe';
import { ThemeCategory } from './utils/recipeFilters';

type Page = 'home' | 'favorites' | 'profile' | 'all-recipes' | 'theme-recipes' | 'diet-admin' | 'admin-panel' | 'admin-category';

type NavigationState = {
  page: Page;
  selectedRecipe?: Recipe;
  showRandomRecipe?: boolean;
  showCategorySelection?: boolean;
  showFridgeRecipes?: boolean;
  selectedCategory?: 'repas' | 'dessert';
  showThemeRecipes?: boolean;
  selectedTheme?: ThemeCategory;
  scrollPosition?: number;
  managedCategory?: string;
};
function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [navigationStack, setNavigationStack] = useState<NavigationState[]>([
    { page: 'home' }
  ]);

  const currentState = navigationStack[navigationStack.length - 1];

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Simple back button handling
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      
      if (navigationStack.length > 1) {
        // Go back in app navigation
        setNavigationStack(prev => prev.slice(0, -1));
      }
      
      // Always push a new state to prevent exit
      window.history.pushState(null, '', window.location.href);
    };

    // Add initial state
    window.history.pushState(null, '', window.location.href);

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigationStack]);

  const pushState = (newState: NavigationState) => {
    setNavigationStack(prev => [...prev, newState]);
    window.history.pushState(null, '', window.location.href);
  };

  const handleBack = () => {
    if (navigationStack.length > 1) {
      const previousState = navigationStack[navigationStack.length - 2];
      setNavigationStack(prev => prev.slice(0, -1));
      
      // Restore scroll position after state update
      if (previousState?.scrollPosition !== undefined) {
        setTimeout(() => {
          window.scrollTo(0, previousState.scrollPosition);
        }, 0);
      }
    }
  };
  const handleRandomRecipe = () => {
    pushState({ 
      page: currentState.page, 
      showCategorySelection: true 
    });
  };

  const handleCategorySelect = (category: 'repas' | 'dessert') => {
    pushState({ 
      page: currentState.page, 
      showRandomRecipe: true, 
      selectedCategory: category 
    });
  };

  const handleFridgeRecipes = () => {
    pushState({ 
      page: currentState.page, 
      showFridgeRecipes: true 
    });
  };

  const handleThemeSelect = (theme: ThemeCategory) => {
    pushState({ 
      page: 'theme-recipes',
      selectedTheme: theme
    });
  };
  const handleRecipeSelect = (recipe: Recipe) => {
    // Save current scroll position
    const scrollPosition = window.scrollY;
    pushState({ 
      page: currentState.page, 
      selectedRecipe: recipe,
      scrollPosition
    });
  };

  const handleViewAllRecipes = () => {
    pushState({ page: 'all-recipes' });
  };

  const handleNavigate = (page: Page) => {
    // Reset to base state for the selected page
    setNavigationStack([{ page }]);
    // Reset scroll position
    window.scrollTo(0, 0);
  };

  const handleManageCategory = (category: string) => {
    pushState({ page: 'admin-category', managedCategory: category });
  };

  if (isLoading) {
    return <SplashScreen />;
  }

  const renderPage = () => {
    if (currentState.selectedRecipe) {
      return (
        <RecipeDetail
          recipe={currentState.selectedRecipe}
          onBack={handleBack}
        />
      );
    }

    switch (currentState.page) {
      case 'home':
        return (
          <>
            <HomePage
              onRandomRecipe={handleRandomRecipe}
              onIngredientSearch={handleFridgeRecipes}
              onRecipeSelect={handleRecipeSelect}
              onViewAllRecipes={handleViewAllRecipes}
              onThemeSelect={handleThemeSelect}
            />
            {currentState.showCategorySelection && (
              <CategorySelection
                onSelectCategory={handleCategorySelect}
                onClose={handleBack}
              />
            )}
            {currentState.showRandomRecipe && (
              <RandomRecipeSwiper
                recipes={recipes}
                category={currentState.selectedCategory || 'repas'}
                onClose={handleBack}
                onViewRecipe={handleRecipeSelect}
              />
            )}
            {currentState.showFridgeRecipes && (
              <FridgeRecipeFinder
                onClose={handleBack}
                onRecipeSelect={handleRecipeSelect}
              />
            )}
          </>
        );
      case 'favorites':
        return (
          <FavoritesPage 
            onRecipeSelect={handleRecipeSelect}
            onNavigate={handleNavigate}
          />
        );
      case 'profile':
        return <ProfilePage onNavigate={handleNavigate} />;
      case 'all-recipes':
        return (
          <AllRecipesPage
            recipes={recipes}
            onRecipeSelect={handleRecipeSelect}
            onBack={handleBack}
          />
        );
      case 'theme-recipes':
        return currentState.selectedTheme ? (
          <ThemeRecipesPage
            recipes={recipes}
            theme={currentState.selectedTheme}
            onRecipeSelect={handleRecipeSelect}
            onBack={handleBack}
          />
        ) : null;
      case 'diet-admin':
        return (
          <AdminDietRecipesPage
            onBack={handleBack}
          />
        );
      case 'admin-panel':
        return (
          <AdminPanel
            onBack={handleBack}
            onManageCategory={handleManageCategory}
          />
        );
      case 'admin-category':
        return currentState.managedCategory ? (
          <AdminCategoryRecipesPage
            category={currentState.managedCategory}
            onBack={handleBack}
          />
        ) : null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderPage()}
      <BottomNav
        currentPage={currentState.page}
        onNavigate={handleNavigate}
      />
    </div>
  );
}

export default App;